// services/tierBranchApi.ts
// API calls for the Tier & Branch feature.
// Tier = transaction band (mostly automatic); Branch = operational group.
// The two are independent dimensions on a user.
import { apiFetch } from '../utils/api';
import type {
  Tier,
  TierInput,
  Branch,
  BranchInput,
  BranchLocks,
  TierBranchUser,
  AutoAssignResult,
  Paginated,
} from '../types/tierBranch';

// ─── shared helpers ─────────────────────────────────────────────

const buildQs = (params: Record<string, string | number | undefined>) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== '' && v !== null) qs.set(k, String(v));
  });
  return qs.toString();
};

// Backend returns { data, meta:{ total, page, limit } }; remap to the
// { data, total, totalPages } envelope every consumer expects (same
// pattern as lordsApi.getAllDetails).
const toPaginated = <T,>(r: any, fallbackLimit: number): Paginated<T> => {
  const limit = r?.meta?.limit || fallbackLimit;
  const total = r?.meta?.total ?? (Array.isArray(r?.data) ? r.data.length : 0);
  return {
    data: r?.data || [],
    total,
    totalPages: Math.max(1, Math.ceil(total / limit)),
  };
};

// ─── Tiers ──────────────────────────────────────────────────────

export const listTiers = () =>
  apiFetch<{ data: Tier[] }>('/lords/tiers').then((r: any) =>
    (r?.data ?? r ?? []) as Tier[]
  );

export const createTier = (payload: TierInput) =>
  apiFetch<Tier>('/lords/tiers', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const updateTier = (id: number | string, payload: Partial<TierInput>) =>
  apiFetch<Tier>(`/lords/tiers/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });

export const updateTierStatus = (
  id: number | string,
  payload: { isActive?: boolean; isManual?: boolean; manualPaymentEnabled?: boolean }
) =>
  apiFetch<Tier>(`/lords/tiers/${id}/status`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });

export const deleteTier = (id: number | string) =>
  apiFetch(`/lords/tiers/${id}`, { method: 'DELETE' });

export const getTierUsers = (
  id: number | string,
  params: { page?: number; limit?: number } = {}
) => {
  const limit = params.limit || 25;
  return apiFetch(
    `/lords/tiers/${id}/users?${buildQs({ page: params.page || 1, limit })}`
  ).then((r: any) => toPaginated<TierBranchUser>(r, limit));
};

/**
 * "Tier Wise Manage User" — bulk-assign users to tiers based on their
 * transaction metrics. Pass { dryRun: true } first to preview the moves,
 * then call again without it to commit. Only ever writes users.tier_id.
 */
export const runTierAutoAssign = (payload: { dryRun?: boolean } = {}) =>
  apiFetch<AutoAssignResult>('/lords/tiers/auto-assign', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

// ─── Branches ───────────────────────────────────────────────────

export const listBranches = (
  params: {
    page?: number;
    limit?: number;
    search?: string;
    /** Only branches not yet attached to any tier. */
    unassigned?: boolean;
  } = {}
) => {
  const limit = params.limit || 25;
  return apiFetch(
    `/lords/branches?${buildQs({
      page: params.page || 1,
      limit,
      search: params.search,
      unassigned: params.unassigned ? 1 : undefined,
    })}`
  ).then((r: any) => toPaginated<Branch>(r, limit));
};

export const createBranch = (payload: BranchInput) =>
  apiFetch<Branch>('/lords/branches', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const updateBranch = (
  id: number | string,
  payload: Partial<BranchInput>
) =>
  apiFetch<Branch>(`/lords/branches/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });

export const deleteBranch = (id: number | string) =>
  apiFetch(`/lords/branches/${id}`, { method: 'DELETE' });

export const updateBranchLocks = (
  id: number | string,
  payload: Partial<BranchLocks>
) =>
  apiFetch<Branch>(`/lords/branches/${id}/locks`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });

export const updateBranchStatus = (
  id: number | string,
  payload: { enabled: boolean }
) =>
  apiFetch<Branch>(`/lords/branches/${id}/status`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });

export const getBranchUsers = (
  id: number | string,
  params: { page?: number; limit?: number } = {}
) => {
  const limit = params.limit || 25;
  return apiFetch(
    `/lords/branches/${id}/users?${buildQs({ page: params.page || 1, limit })}`
  ).then((r: any) => toPaginated<TierBranchUser>(r, limit));
};

/** Add existing user(s) to a branch. */
export const addUsersToBranch = (
  id: number | string,
  payload: { userIds: number[] }
) =>
  apiFetch(`/lords/branches/${id}/users`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });

/** Shift user(s) from their current branch to another branch. */
export const transferBranchUsers = (payload: {
  userIds: number[];
  toBranchId: number;
  reason?: string;
}) =>
  apiFetch('/lords/branches/transfer', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

/**
 * Manually assign user(s) to a new tier AND/OR branch in one call (the manual
 * counterpart to auto-assign). Include `tierId` and/or `branchId`; pass `null`
 * to clear that membership. Writes 'manual' audit rows for changed dimensions.
 */
export const assignUser = (payload: {
  userIds?: number[];
  userId?: number;
  tierId?: number | null;
  branchId?: number | null;
  reason?: string;
}) =>
  apiFetch<{ status: string; updated: number; notFound: number[] }>('/lords/users/assign', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

// ─── Branch balance settlement (branch ⇄ super admin) ───────────
export interface SettlementSummary {
  branchId: number; branchName: string;
  grossDeposits: number; depositCount: number;
  grossWithdrawals: number; withdrawalCount: number;
  netPnL: number; outstanding: number;
  creditLimit: number; availableCredit: number | null; overLimit: boolean;
  recentSettlements: Settlement[];
}
export interface BranchTxn {
  kind: 'deposit' | 'withdraw'; id: number; username: string; amount: number;
  status: string; at: string; ref: string | null; remark: string | null;
  actionBy: string | null; handledAt: string | null;
}
export interface Settlement {
  id: number; amount: number; direction: 'collect' | 'payout'; note: string;
  netPnlSnapshot: number; outstandingBefore: number; outstandingAfter: number;
  settledBy: number | null; settledByName: string | null; createdAt: string;
}

export const getBranchSettlement = (id: number) =>
  apiFetch<SettlementSummary>(`/lords/branches/${id}/settlement`);

export const getBranchTransactions = (
  id: number, params: { type?: string; status?: string; page?: number; limit?: number } = {}
) => {
  const limit = params.limit || 25;
  return apiFetch(
    `/lords/branches/${id}/transactions?${buildQs({ type: params.type, status: params.status, page: params.page || 1, limit })}`
  ).then((r: any) => toPaginated<BranchTxn>(r, limit));
};

export const getBranchSettlements = (id: number, params: { page?: number; limit?: number } = {}) => {
  const limit = params.limit || 50;
  return apiFetch(`/lords/branches/${id}/settlements?${buildQs({ page: params.page || 1, limit })}`)
    .then((r: any) => toPaginated<Settlement>(r, limit));
};

export const createBranchSettlement = (
  id: number, payload: { amount: number; direction: 'collect' | 'payout'; note?: string }
) =>
  apiFetch<{ status: string; settlement: Settlement; balance: SettlementSummary }>(
    `/lords/branches/${id}/settlements`, { method: 'POST', body: JSON.stringify(payload) }
  );

// CSV download (auth-protected raw fetch → triggers a browser download)
export async function downloadSettlementReport(id: number, branchName?: string) {
  const token = localStorage.getItem('token');
  const res = await fetch(`https://api.playbro.com/lords/branches/${id}/settlements/report`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error('Report download failed');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `settlement_${(branchName || 'branch').replace(/\s+/g, '_')}_${id}.csv`;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}
